// Fuel surcharge for the Billing tab — the rate schedule's Fuel Surcharge
// Index and Fuel Surcharge Provisions turned into a number on a summary.
//
// The provisions (billingRateSchedule.js FUEL_RULES) say:
//   • the equipment % adder comes from the US Gulf Coast (PADD 3) on-highway
//     diesel price, averaged over the 13 weekly EIA reports of a quarter;
//   • that quarter's adder engages on the 1st of the month after the quarter
//     ends and holds until the next quarter's replaces it — so work dated in
//     Q3 bills at the adder set by Q2's average;
//   • the adder applies ONLY to equipment subtotals — not labor, per diem or
//     materials — and nothing is credited below the $3.75 threshold.
//
// Rate lookup reads the job's own (editable) fuel index rows, so a job on a
// different index bills off its own table.
//
// The adder is CONTINUOUS, not stepped. The rate schedule prints an index
// table, but the contract's own workbook computes the column from one formula:
//
//     =IF(price < 3.75, 0, ((price - 3.75) / 0.05) * 0.67%)
//
// so every fraction of a $0.05 increment earns its share and the whole curve
// is anchored at the $3.75 threshold, not re-anchored at the bottom of the
// printed table. The printed rows are that formula rounded for display
// ($4.25 -> 10 x 0.67 = 6.7%; $3.90 -> 3 x 0.67 = 2.01 ~ 2.0%), which is why
// they are kept as the editable definition of threshold and rate per step
// rather than as lookup points.
//
// Equipment lines on a summary freeze the adder they billed at (snapshot
// `fs`) the way markup freezes, so a later price or index edit never moves
// issued paper. Lines without a frozen adder (unbilled, or on summaries cut
// before the surcharge existed) price live.

const pNum = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[$,%\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
};
const round1 = (n) => Math.round(n * 10) / 10;
const round2 = (n) => { const v = Number(n) || 0; return Math.round((v + Number.EPSILON * v) * 100) / 100; };

// EIA series EMD_EPD2D_PTE_R30_DPG (Weekly Gulf Coast No 2 Diesel Retail
// Prices, $/gal), last 13 weekly reports of each calendar quarter, to four
// decimals — the adder is continuous, so a rounder average moves the billed
// percentage. Refresh
// with localserver/scripts/eia-diesel-quarterly.js once a quarter closes; a
// job can also enter a quarter's price in Rates & Rules before this ships.
export const EIA_PADD3_QUARTERLY = {
    '2024-Q1': 3.6915,
    '2024-Q2': 3.5738,
    '2024-Q3': 3.3528,
    '2024-Q4': 3.203,
    '2025-Q1': 3.3409,
    '2025-Q2': 3.22,
    '2025-Q3': 3.3997,
    '2025-Q4': 3.3412,
    '2026-Q1': 3.8355,
    '2026-Q2': 4.9487,
    '2026-Q3': 5.3058,
};

// Job settings blob (settings.fuel):
//   on     — bill the surcharge at all
//   exFuel — keep 52300 Fuel purchases out of the base; fuel billed at cost
//            already carries today's price, surcharging it double-charges
//   q      — per-job quarter price overrides/additions, { 'YYYY-Qn': $/gal }
export const FUEL_SETTINGS_DEFAULT = { on: true, exFuel: true, q: {} };

export const FUEL_COST_TYPE = '52300';

export const quarterOf = (ymd) => {
    const s = String(ymd || '');
    const y = Number(s.slice(0, 4));
    const m = Number(s.slice(5, 7));
    if (!y || !m) return '';
    return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
};

export const shiftQuarter = (q, by) => {
    const mt = /^(\d{4})-Q([1-4])$/.exec(String(q || ''));
    if (!mt) return '';
    const idx = Number(mt[1]) * 4 + (Number(mt[2]) - 1) + by;
    return `${Math.floor(idx / 4)}-Q${(idx % 4) + 1}`;
};

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// 'Jul–Sep 2026' for a quarter.
export const quarterSpan = (q) => {
    const mt = /^(\d{4})-Q([1-4])$/.exec(String(q || ''));
    if (!mt) return '';
    const i = (Number(mt[2]) - 1) * 3;
    return `${MON[i]}–${MON[i + 2]} ${mt[1]}`;
};

// The index rows → { below, points, ext }. Rows are { name, adder } strings
// as the Rates & Rules card holds them: '< $3.75' / '0.0%', '$3.80' / '0.7%',
// '> $4.25' / 'Additional 0.67% per $0.05 of increase'.
export const parseFuelIndex = (rows) => {
    const points = [];
    let below = null;
    let ext = null;
    (rows || []).forEach((r) => {
        const mt = /([<>])?\s*=?\s*\$?\s*(\d+(?:\.\d+)?)/.exec(String(r?.name || ''));
        if (!mt) return;
        const op = mt[1] || '';
        const price = Number(mt[2]);
        const adder = String(r?.adder ?? '');
        const per = /(\d*\.?\d+)\s*%.*?per\s*\$?\s*(\d*\.?\d+)/i.exec(adder);
        if (op === '>' && per) {
            ext = { from: price, inc: Number(per[1]), step: Number(per[2]) };
        } else if (op === '<') {
            below = { price, pct: pNum(adder) };
        } else if (!op) {
            points.push({ price, pct: pNum(adder) });
        }
    });
    points.sort((a, b) => a.price - b.price);
    return { below, points, ext };
};

// Equipment % adder for an average $/gal price, off the parsed index.
//
// Two decimals, because that is what the contract's log carries: 2026-Q1's
// $3.835462 average is 1.15%, not 1.1% or 1.2%.
export const adderPct = (price, rows) => {
    const p = Number(price);
    if (!Number.isFinite(p) || p <= 0) return 0;
    const { below, points, ext } = parseFuelIndex(rows);
    // Threshold: the "< $X" row. Without one, the cheapest printed row sits
    // one step above the threshold, so back into it.
    const threshold = below ? below.price
        : points.length && ext ? points[0].price - ext.step
            : points.length ? points[0].price : 0;
    if (p < threshold) return round2(below ? below.pct : 0);
    // The "additional N% per $M of increase" row defines the whole curve.
    if (ext && ext.step > 0) return round2(((p - threshold) / ext.step) * ext.inc);
    // An index with no such row is a pure lookup table: take the lower row.
    const EPS = 1e-9;
    let pct = below ? below.pct : 0;
    points.forEach((pt) => { if (pt.price <= p + EPS) pct = pt.pct; });
    return round2(pct);
};

// The fuel adder printed the way it is billed: two decimals, with a
// trailing zero dropped so 6.7% does not read as 6.70%.
export const fmtPct = (n) => `${Number(Number(n).toFixed(2))}%`;

export const fuelPrices = (fuelSettings) => ({ ...EIA_PADD3_QUARTERLY, ...((fuelSettings && fuelSettings.q) || {}) });

// The adder in force on a work date: the prior quarter's average through the
// index. { q: price quarter, price, pct, missing } — missing when that
// quarter's price isn't known yet (pct 0 until someone enters it).
export const fuelRateFor = (ymd, { settings, rows } = {}) => {
    const q = shiftQuarter(quarterOf(ymd), -1);
    if (!q) return { q: '', price: null, pct: 0, missing: false };
    const price = fuelPrices(settings)[q];
    if (price == null || price === '' || !(Number(price) > 0)) return { q, price: null, pct: 0, missing: true };
    return { q, price: Number(price), pct: adderPct(price, rows), missing: false };
};

// Why a line is out of the surcharge base, or '' when it's in. Equipment
// class only; fuel purchases out when the job excludes them (they already
// bill at today's pump price); pass-through freight (billingPassThru) always
// out — it re-bills at cost; and equipment the rate sheet flags as carrying
// no engine of its own out, since there is no diesel to recover on it.
// `cls` is billing.js's cost class, `fu` the rate sheet's fuel flag
// (undefined = the line never resolved to a rate sheet row, which stays in
// the base rather than quietly dropping off it).
export const fuelExcluded = (t, settings) => {
    if (!t || t.nb || t.cls !== 'Equipment') return 'other';
    if (t.pt) return 'passThru';
    const fs = { ...FUEL_SETTINGS_DEFAULT, ...(settings || {}) };
    if (fs.exFuel && String(t.ct) === FUEL_COST_TYPE) return 'fuelBuy';
    if (t.fu === false) return 'noFuel';
    return '';
};

export const fuelEligible = (t, settings) => !fuelExcluded(t, settings);

// The adder to freeze on a line landing on a summary — undefined when the
// line doesn't carry the surcharge or its quarter has no price yet (it then
// keeps pricing live until one is entered).
export const fuelSnapPct = (t, ctx = {}) => {
    const fs = { ...FUEL_SETTINGS_DEFAULT, ...(ctx.settings || {}) };
    if (!fs.on || !fuelEligible(t, fs)) return undefined;
    const r = fuelRateFor(t.date, { settings: fs, rows: ctx.rows });
    return r.missing ? undefined : r.pct;
};

// One line's share of the surcharge: its bill amount at the adder frozen on it,
// or the live adder for its work date. Same gates as fuelSurcharge() below, so
// the shares add up to its `amt` (to the cent or two its per-period rounding
// moves) — for a report that carries the surcharge inside each equipment line
// instead of as a row of its own.
export const fuelLineAmt = (t, ctx = {}) => {
    if (!t || t.nb) return 0;
    const fs = { ...FUEL_SETTINGS_DEFAULT, ...(ctx.settings || {}) };
    const frozen = t.fs != null && t.fs !== '' && Number.isFinite(Number(t.fs));
    if (!frozen && (!fs.on || fuelExcluded(t, fs))) return 0;
    const pct = frozen ? Number(t.fs) : fuelRateFor(t.date, { settings: fs, rows: ctx.rows }).pct;
    return ((Number(t.bill) || 0) * (Number(pct) || 0)) / 100;
};

// Surcharge across a summary's lines, split by the adder period each line's
// work date falls in. Lines with a frozen `fs` bill at that; the rest price
// live. Returns { amt, base, n, periods: [{ q, span, price, pct, base, amt,
// n, frozen, missing }], missing: [quarters], excl, unk }.
//
// `excl` is the equipment that stayed OUT of the base and why — what the
// internal summary shows so the number can be defended line for line:
//   noFuel   — rate sheet says the machine burns no fuel
//   fuelBuy  — 52300 fuel purchases, already at pump price
//   passThru — pass-through freight/delivery, which re-bills at cost
// Together with `base` they account for every equipment line on the summary,
// so the internal summary's table reconciles top to bottom.
// `unk` is equipment in the base that never resolved to a rate sheet row, so
// its fuel flag is a guess — it bills, but the summary calls it out.
export const fuelSurcharge = (lines, ctx = {}) => {
    const fs = { ...FUEL_SETTINGS_DEFAULT, ...(ctx.settings || {}) };
    const byKey = {};
    const excl = { noFuel: { base: 0, n: 0 }, fuelBuy: { base: 0, n: 0 }, passThru: { base: 0, n: 0 } };
    const unk = { base: 0, n: 0 };
    (lines || []).forEach((t) => {
        if (!t || t.nb) return;
        const frozen = t.fs != null && t.fs !== '' && Number.isFinite(Number(t.fs));
        const why = fuelExcluded(t, fs);
        // Equipment held out of the base is tallied whether or not the job
        // bills the surcharge at all — a job with `on: false` still shows
        // what the base would have been.
        if (!frozen && why && excl[why]) {
            excl[why].base += Number(t.bill) || 0;
            excl[why].n += 1;
        }
        if (!frozen && (!fs.on || why)) return;
        if (!frozen && t.fu === undefined) { unk.base += Number(t.bill) || 0; unk.n += 1; }
        const live = fuelRateFor(t.date, { settings: fs, rows: ctx.rows });
        const pct = frozen ? Number(t.fs) : live.pct;
        const k = `${live.q}|${pct}|${frozen ? 'f' : live.missing ? 'm' : 'l'}`;
        const p = byKey[k] || (byKey[k] = {
            q: live.q,
            span: quarterSpan(shiftQuarter(live.q, 1)),
            price: live.price,
            pct,
            base: 0,
            n: 0,
            frozen,
            missing: !frozen && live.missing,
        });
        p.base += Number(t.bill) || 0;
        p.n += 1;
    });
    const periods = Object.values(byKey)
        .map((p) => ({ ...p, base: round2(p.base), amt: round2((p.base * p.pct) / 100) }))
        .sort((a, b) => String(a.q).localeCompare(String(b.q)) || a.pct - b.pct);
    return {
        periods,
        amt: round2(periods.reduce((s, p) => s + p.amt, 0)),
        base: round2(periods.reduce((s, p) => s + p.base, 0)),
        n: periods.reduce((s, p) => s + p.n, 0),
        missing: [...new Set(periods.filter((p) => p.missing).map((p) => p.q))],
        excl: {
            noFuel: { base: round2(excl.noFuel.base), n: excl.noFuel.n },
            fuelBuy: { base: round2(excl.fuelBuy.base), n: excl.fuelBuy.n },
            passThru: { base: round2(excl.passThru.base), n: excl.passThru.n },
        },
        unk: { base: round2(unk.base), n: unk.n },
    };
};
